import { Random } from '../../Utility/Random';
import Game from '../../Game';
import { Howl } from 'howler';

export interface IAudioParams {
    loop: boolean;
    volume: number;
    seek: number;
}

export enum AudioTrackType {
    SFX = 'sfx',
    MUSIC = 'music',
}

const defaultParams: IAudioParams = {
    loop: false,
    volume: 1,
    seek: 0,
};

/**
 * Key-map for audio sprite lookup.
 * Populated by HowlerPixiLoaderMiddleware.ts.
 */
export const AudioSpriteMap: { [key: string]: string[] } = {};

export default class AudioClip {
    public readonly id: string;
    public readonly name: string;
    public track: AudioTrackType;

    public howl: Howl;
    private soundID: number;

    private readonly sprite: string;
    private _loop: boolean = false;
    private _volume: number = 1;

    private _onCompleteCB: (clip: AudioClip) => void;

    constructor(
        clipName: string,
        track: AudioTrackType,
        params?: Partial<IAudioParams>
    ) {
        this.id = Random.ID();
        this.name = clipName;
        this.track = track;

        for (const key in AudioSpriteMap) {
            if (AudioSpriteMap[key].indexOf(clipName) !== -1) {
                this.howl = this.getHowlResource(key);
                this.sprite = clipName;
                if (this.howl) break;
            }
        }

        if (!this.howl) {
            this.howl = this.getHowlResource(clipName);
        }

        if (params) {
            params = {
                ...defaultParams,
                ...params,
            };
        } else {
            params = defaultParams;
        }

        this.loop = params.loop;
        this.volume = params.volume;
    }

    public onComplete(cb: (clip: AudioClip) => void) {
        this._onCompleteCB = cb;
    }

    public play() {
        if (!this.howl) {
            console.log('Howl is not defined.', this.howl);
            return;
        }

        // Don't play SFX if the volume is 0.
        if (this.track === AudioTrackType.SFX && this.volume >= 0.01) {
            this.soundID = this.howl.play(this.sprite);
            this.howl.volume(this.volume, this.soundID);
            this.howl.once(
                'end',
                () => {
                    /* Manually release HTML5 Audio.
                    const sound = (this.howl as any)._sounds.find(
                        (s: any) => s._id === this.soundID
                    );
                    if (sound) {
                        (Howler as any)._releaseHtml5Audio(sound._node);
                    }
                     */
                    if (this._onCompleteCB) this._onCompleteCB(this);
                },
                this.soundID
            );
        }

        if (
            this.track === AudioTrackType.MUSIC &&
            (!this.soundID || !this.howl.playing(this.soundID))
        ) {
            this.soundID = this.howl.play();
            this.howl.volume(this.volume, this.soundID);
            this.howl.loop(this.loop, this.soundID);
            this.howl.once(
                'playerror',
                (e, m) => {
                    console.warn(e, m, 'onplayerror');
                    this.howl.once('unlock', () => {
                        this.play();
                    });
                },
                this.soundID
            );
        }
    }

    public destroy() {
        if (!this.howl) {
            return;
        }

        this.howl.stop(this.soundID);
    }

    get volume(): number {
        return this._volume;
    }

    set volume(value: number) {
        this._volume = value;

        if (this.howl) {
            if (this.soundID) {
                this.howl.volume(value, this.soundID);
            } else {
                this.howl.volume(value);
            }
        }
    }

    get loop(): boolean {
        return this._loop;
    }

    set loop(value: boolean) {
        this._loop = value;

        if (this.howl) {
            if (this.soundID) {
                this.howl.loop(value, this.soundID);
            } else {
                this.howl.loop(value);
            }
        }
    }

    private getHowlResource(id: string): Howl {
        if (Game.resource.existsAndLoaded(id)) {
            const resource: any = Game.resource.get(id);
            if (resource instanceof Howl) {
                return Game.resource.get(id);
            }
        }
        return null;
    }
}
